/**
 * Enemy Ship - AI-controlled enemy ship
 */

import * as THREE from 'three';
import { modelLoader } from '../systems/ModelLoader.js';
import { normalizeShipScale } from '../utils/shipScale.js';
import { GAME_BOUNDARY_SIZE } from '../utils/constants.js';
import {
    clampLevel,
    getLevelData,
    getEnemyModelForLevel,
    getCoinDropForLevel
} from '../config/shipLevels.js';

const Y_AXIS = new THREE.Vector3(0, 1, 0);

export class EnemyShip {
    constructor(options = {}) {
        this.game = options.game;
        this.position = options.position || new THREE.Vector3(0, 0, 0);

        this.level = clampLevel(options.level || 1);
        this.speed = 8;
        this.baseSpeed = this.speed;
        this.chaseSpeedMultiplier = 1.35;
        this.id = options.id || Math.random().toString(36).substr(2, 9);
        this.name = options.name || `Enemy ${Math.floor(Math.random() * 1000)}`;
        this.baseName = this.name;

        // AI personality: 'aggressive' 主动攻击玩家, 'passive' 只巡逻不主动攻击
        this.aiPersonality = options.aiPersonality || 'aggressive';

        // AI properties
        this.targetPosition = new THREE.Vector3();
        this.nextWaypointTime = 0;
        this.waypointInterval = 3000; // 3秒换一个目标
        this.chaseOffset = this.generateChaseOffset();
        this.chaseOffsetInterval = 5000 + Math.random() * 4000;
        this.nextChaseOffsetTime = 0;
        this.faction = 'enemy';
        this.collectedCoins = 0;
        this.lastAttacker = null;
        this.killerRef = null;
        this.killerFaction = null;

        // 敌船副船系统
        this.escorts = []; // 副船数组
        this.maxEscorts = 5; // 敌船最多5艘副船
        this.escortAttackPower = 3; // 副船攻击力（降低以提升玩家生存能力）

        // 攻击目标选择
        this.currentShootingTarget = null; // 当前射击目标
        this.targetLockTime = 0; // 目标锁定开始时间
        this.targetLockDuration = 3000 + Math.random() * 4000; // 锁定目标3-7秒再切换
        this.patrolRadius = 40;
        this.sightRange = 70; // 视野范围，内玩家优先被追击
        this.playerChaseTimeout = 2500; // ms，当玩家离开视野后继续追击的时间
        this.lastPlayerSightTime = 0;
        this.lastKnownPlayerPosition = new THREE.Vector3();
        this.isChasingPlayer = false;

        // Combat properties for AI shooting（由等级数据驱动）
        this.cannonCooldown = 5.5;
        this.lastFireTime = 0;
        this.shootingRange = 40;
        this.shootingAccuracy = 0.7;
        this.attackPower = 1;
        this.coinValue = getCoinDropForLevel(this.level);

        // Three.js objects
        this.mesh = null;
        this.body = null;

        // Hit effect throttle
        this.lastHitEffect = 0;
        this.hitEffectCooldown = 120; // ms

        // Reusable buffers to control GC pressure during AI updates
        this._scratch = {
            myPos: new THREE.Vector3(),
            aux: new THREE.Vector3(),
            aux2: new THREE.Vector3(),
            aux3: new THREE.Vector3(),
            separation: new THREE.Vector3(),
            push: new THREE.Vector3(),
            ui: new THREE.Vector3(),
            quat: new THREE.Quaternion()
        };
        this._targetBuffer = {
            position: new THREE.Vector3(),
            name: '',
            type: '',
            entity: null,
            distance: 0
        };

        this._updatePhase = Math.floor(Math.random() * 3);
        this._frameCounter = Math.floor(Math.random() * 60);
        this._hasPassiveTarget = false;
        this._lastPassiveTarget = new THREE.Vector3();
        this.isPrimaryAggressor = false;

        this.applyLevelStats(this.level);
    }

    applyLevelStats(level) {
        const data = getLevelData(level);
        this.level = data.level;
        this.levelData = data;
        this.maxHealth = data.health;
        this.health = data.health;
        // 降低敌船攻击力20%以提升玩家生存能力
        this.attackPower = Math.round(data.attack * 0.8);
        this.coinValue = getCoinDropForLevel(level);

        this.updateDisplayName();

        this.baseSpeed = 7 + (this.level - 1) * 0.4;
        this.speed = this.baseSpeed;
        // 敌船射击冷却时间：3.8-5.8秒，进一步降低对玩家的压迫感
        this.cannonCooldown = Math.max(3.8, 5.8 - (this.level - 1) * 0.25);
        this.shootingRange = 26 + this.level * 1.5;
        // 降低敌船精准度以提升玩家生存能力：0.35-0.7（原来0.5-0.9）
        this.shootingAccuracy = Math.min(0.35 + this.level * 0.035, 0.7);
        this.lastFireTime = 0;

        if (this.body) {
            const damping = this.level >= 7 ? 0.42 : this.level >= 4 ? 0.48 : 0.55;
            this.body.linearDamping = damping;
        }
    }

    updateDisplayName() {
        if (!this.baseName) {
            this.baseName = this.name;
        }
        this.name = `${this.baseName} [L${this.level}]`;

        if (this.game?.enemyUIManager?.enemyUIs) {
            const ui = this.game.enemyUIManager.enemyUIs.get(this.id);
            if (ui && ui.nameLabel) {
                ui.nameLabel.textContent = this.name;
            }
        }
    }

    async init() {
        // 先应用等级数据，确保属性正确
        this.applyLevelStats(this.level);
        console.log(`Enemy ${this.name} initialized: L${this.level}, HP:${this.maxHealth}, ATK:${this.attackPower}, AI:${this.aiPersonality}`); // 调试信息
        await this.createMesh();
        this.createPhysicsBody();
        this.setInitialPosition();
        // 注册到屏幕空间UI管理器
        this.registerToUIManager();
        this.setRandomWaypoint();
    }

    registerToUIManager() {
        // 在屏幕空间UI管理器中注册这个敌船
        if (this.game.enemyUIManager) {
            this.game.enemyUIManager.createEnemyUI(this.id, this.name, this.maxHealth);
        }
    }

    async createMesh() {
        try {
            const modelName = getEnemyModelForLevel(this.level);
            const enemyModel = await modelLoader.loadModel(modelName, {
                position: { x: this.position.x, y: this.position.y, z: this.position.z },
                rotation: { x: 0, y: 0, z: 0 },
                castShadow: false, // dynamic shadow disabled for performance
                receiveShadow: true
            });

            this.mesh = enemyModel;
            normalizeShipScale(this.mesh);

            // 增加敌船模型亮度
            this.mesh.traverse((child) => {
                if (child.isMesh && child.material) {
                    const materials = Array.isArray(child.material) ? child.material : [child.material];
                    materials.forEach(mat => {
                        if (mat) {
                            // 增加自发光使模型更亮
                            if (mat.emissive) {
                                mat.emissive.setHex(0x333333); // 添加灰色自发光
                            }
                            // 如果材质支持，增加亮度
                            if (mat.color) {
                                mat.color.multiplyScalar(1.3); // 颜色亮度提升30%
                            }
                        }
                    });
                }
            });

        } catch (error) {
            console.warn('Failed to load enemy ship model, falling back to basic mesh:', error);
            this.createBasicMesh();
            return;
        }

        // Tag for identification
        this.mesh.userData = { type: 'enemy', entity: this };
        this.mesh.position.copy(this.position);
    }

    createBasicMesh() {
        // Create simple enemy ship (fallback)
        const hullGeometry = new THREE.BoxGeometry(2.2, 0.8, 4.2);
        const hullMaterial = new THREE.MeshLambertMaterial({
            color: 0x8B0000 // Dark red for enemies
        });

        this.mesh = new THREE.Mesh(hullGeometry, hullMaterial);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;

        // 敌船船头标识 (黄色尖头)
        const bowGeometry = new THREE.ConeGeometry(0.8, 1.6);
        const bowMaterial = new THREE.MeshLambertMaterial({
            color: 0xFFFF00 // 黄色船头
        });
        const bowMesh = new THREE.Mesh(bowGeometry, bowMaterial);
        bowMesh.position.set(0, 0.8, 2.4);
        bowMesh.rotateX(Math.PI / 2);
        this.mesh.add(bowMesh);

        normalizeShipScale(this.mesh);

        // Add enemy tag for identification
        this.mesh.userData = { type: 'enemy', entity: this };
    }

    createPhysicsBody() {
        // Create physics body - 增大碰撞体积以匹配视觉模型
        let size = { width: 3.2, height: 1.0, length: 6.5 };
        if (this.level >= 4 && this.level <= 6) {
            size = { width: 4.2, height: 1.2, length: 8.5 };
        } else if (this.level >= 7) {
            size = { width: 5.2, height: 1.4, length: 10.5 };
        }

        this.body = this.game.physicsSystem.createShipBody(this.position, size);
        this.applyLevelStats(this.level);
        this.body.collisionFilterGroup = 2; // 敌船碰撞组
        this.body.collisionFilterMask = 1 | 2 | 4 | 8; // 恢复与玩家/其他船只的碰撞，以保持战斗体验

        // Add entity reference for damage detection
        this.body.userData = { type: 'enemy', entity: this };

        // Add collision event listener
        this.body.addEventListener('collide', (event) => {
            this.handleCollision(event);
        });

        // 将物理体添加到物理世界中 - 这是关键步骤！
        this.game.physicsSystem.addBody(this.body);
        console.log('✅ 敌船物理体已添加到物理世界');
    }

    setInitialPosition() {
        this.mesh.position.copy(this.position);
        this.body.position.set(this.position.x, this.position.y, this.position.z);
    }

    update(deltaTime) {
        // 性能优化：根据与玩家距离动态调整物理和AI更新
        const player = this.game?.getGameObject ? this.game.getGameObject('player') : null;
        const distanceToPlayer = player && player.body && this.body
            ? this.body.position.distanceTo(player.body.position)
            : 0;

        // 远距离优化：超过70单位的敌船降低更新频率
        const isFarAway = distanceToPlayer > 70;

        // AI behavior
        this._frameCounter = (this._frameCounter + 1) % 600;
        const allowHeavyAI = ((this._frameCounter + this._updatePhase) % 3) === 0;

        // 远距离敌船跳过部分帧的AI更新
        if (!isFarAway || this._frameCounter % 5 === 0) {
            this.updateAI(deltaTime, allowHeavyAI);
        }

        // Update movement
        this.updateMovement(deltaTime);

        // Update rotation (跟玩家船一样)
        this.updateRotation(deltaTime);

        // Sync mesh with physics body (确保Y轴固定在海平面以上0.1单位)
        if (this.body && this.mesh) {
            this.mesh.position.x = this.body.position.x;
            this.mesh.position.y = 0.1; // 与海洋平面匹配
            this.mesh.position.z = this.body.position.z;
            this.mesh.quaternion.copy(this.body.quaternion);

            // 同时确保物理体Y位置固定
            this.body.position.y = 0.1;

            // 性能优化：远距离敌船提高休眠倾向
            if (isFarAway && this.body.sleepState !== 2) { // 2 = CANNON.Body.SLEEPING
                this.body.sleepSpeedLimit = 0.3; // 提高休眠速度阈值
                this.body.sleepTimeLimit = 0.5; // 缩短休眠等待时间
            } else if (!isFarAway) {
                this.body.sleepSpeedLimit = 0.25; // 恢复默认值
                this.body.sleepTimeLimit = 0.9;
            }
        }

        // 更新副船
        this.updateEscorts(deltaTime);

        // 更新屏幕空间UI
        this.updateScreenSpaceUI();
    }

    updateScreenSpaceUI() {
        // 更新屏幕空间UI管理器中的敌船UI
        if (this.game.enemyUIManager && this.body) {
            const worldPosition = this._scratch.ui.copy(this.body.position);
            this.game.enemyUIManager.updateEnemyUI(
                this.id,
                worldPosition,
                this.health,
                this.maxHealth
            );
        }
    }

    updateAI(deltaTime, allowHeavyAI = true) {
        const now = Date.now();

        if (this.lastFireTime > 0) {
            this.lastFireTime -= deltaTime * 1000;
            if (this.lastFireTime < 0) {
                this.lastFireTime = 0;
            }
        }

        const shouldTargetPlayer = this.aiPersonality === 'aggressive';
        let chasingTarget = false;
        const scratch = this._scratch;
        const myPos = scratch.myPos;

        if (this.body) {
            myPos.copy(this.body.position);

            if (shouldTargetPlayer) {
                const player = this.game?.getGameObject ? this.game.getGameObject('player') : null;
                if (player && player.body && player.health > 0) {
                    const playerPos = scratch.aux.copy(player.body.position);
                    const distanceToPlayer = myPos.distanceTo(playerPos);

                    if (distanceToPlayer <= this.sightRange * 0.6) {
                        const slotGranted = this.game.enemyManager?.requestAggressorSlot(this);
                        if (slotGranted) {
                            this.refreshChaseOffset(now);
                            this.targetPosition.copy(this.getChasePositionTowards(playerPos, scratch.aux2));
                            chasingTarget = true;
                            this.isPrimaryAggressor = true;
                        } else {
                            this.isPrimaryAggressor = false;
                        }
                    }
                }
            } else {
                let shouldChasePassive = false;
                if (allowHeavyAI) {
                    const nearestDistance = this.findNearestEnemy(myPos, scratch.aux);
                    if (nearestDistance !== null && nearestDistance < this.sightRange * 0.5) {
                        this._lastPassiveTarget.copy(scratch.aux);
                        this._hasPassiveTarget = true;
                        shouldChasePassive = true;
                    } else if (nearestDistance === null || nearestDistance > this.sightRange * 0.75) {
                        this._hasPassiveTarget = false;
                    }
                } else if (this._hasPassiveTarget) {
                    shouldChasePassive = true;
                }

                if (this._hasPassiveTarget && shouldChasePassive) {
                    this.refreshChaseOffset(now);
                    this.targetPosition.copy(this.getChasePositionTowards(this._lastPassiveTarget, scratch.aux2));
                    chasingTarget = true;
                }
                this.isPrimaryAggressor = false;
            }
        }

        if (chasingTarget) {
            this.speed = this.baseSpeed * this.chaseSpeedMultiplier;
            const separation = this.computeSeparationOffset(myPos, scratch.separation);
            const separationStrength = separation.lengthSq();
            if (separationStrength > 0) {
                if (separationStrength > 25) {
                    this.chaseOffset = this.generateChaseOffset(true);
                    this.nextChaseOffsetTime = now + this.chaseOffsetInterval;
                }
                this.targetPosition.add(separation);
                const boundaryLimit = GAME_BOUNDARY_SIZE - 5;
                this.targetPosition.x = Math.max(-boundaryLimit, Math.min(boundaryLimit, this.targetPosition.x));
                this.targetPosition.z = Math.max(-boundaryLimit, Math.min(boundaryLimit, this.targetPosition.z));
                this.targetPosition.y = 0.1;
            }
        } else {
            this.speed = this.baseSpeed;

            if (now > this.nextWaypointTime) {
                this.setRandomWaypoint();
                this.nextWaypointTime = now + this.waypointInterval;
            }
        }

        this.checkPlayerAndShoot(allowHeavyAI);
    }

    updateRotation(deltaTime) {
        if (!this.body) return;

        // 跟玩家船一样的旋转逻辑：直接朝向目标位置
        const scratch = this._scratch;
        const currentPos = scratch.myPos.copy(this.body.position);
        const direction = scratch.aux.subVectors(this.targetPosition, currentPos);
        direction.y = 0; // 确保只在X-Z平面计算

        const distanceSq = direction.lengthSq();
        if (distanceSq > 9) { // 距离阈值 (3^2)
            direction.normalize();
            const targetAngle = Math.atan2(direction.x, direction.z);

            // 直接设置船只角度，跟玩家船一样
            const targetQuaternion = scratch.quat;
            targetQuaternion.setFromAxisAngle(Y_AXIS, targetAngle);

            // 直接设置物理体的旋转
            this.body.quaternion.copy(targetQuaternion);
        }

        // 停止角速度，跟玩家船一样
        this.body.angularVelocity.set(0, 0, 0);
    }

    updateMovement(deltaTime) {
        if (!this.body) return;

        const scratch = this._scratch;
        const currentPos = scratch.myPos.copy(this.body.position);
        const direction = scratch.aux.subVectors(this.targetPosition, currentPos);
        direction.y = 0;

        const distanceSq = direction.lengthSq();

        if (distanceSq > 9) {
            direction.normalize();

            const forceStrength = 6 * this.body.mass;
            this.game.physicsSystem.applyShipMovementForce(
                this.body,
                direction,
                forceStrength
            );

            const velocity = this.body.velocity;
            const currentSpeedSq = velocity.x * velocity.x + velocity.z * velocity.z;
            const maxSpeedSq = this.speed * this.speed;

            if (currentSpeedSq > maxSpeedSq && currentSpeedSq > 0) {
                const currentSpeed = Math.sqrt(currentSpeedSq);
                const scale = this.speed / currentSpeed;
                velocity.x *= scale;
                velocity.z *= scale;
            }

            velocity.y = 0;
        } else {
            const velocity = this.body.velocity;
            velocity.x *= 0.9;
            velocity.z *= 0.9;
            velocity.y = 0;
        }
    }

    setRandomWaypoint() {
        // Use current body position for waypoint calculation
        const currentPos = this.body ? this.body.position : this.position;

        // Simple random waypoint selection (跟之前一样简单)
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * this.patrolRadius + 10;

        this.targetPosition.set(
            currentPos.x + Math.cos(angle) * distance,
            0,
            currentPos.z + Math.sin(angle) * distance
        );

        // Keep within bounds (确保在游戏边界内)
        this.targetPosition.x = Math.max(-95, Math.min(95, this.targetPosition.x));
        this.targetPosition.z = Math.max(-95, Math.min(95, this.targetPosition.z));
    }

    takeDamage(damage = 1, source = null) {
        if (source) {
            this.lastAttacker = source;
            this.killerFaction = source.faction || this.killerFaction;
            // 调试日志
            console.log(`${this.name} took ${damage} damage from:`, {
                source: source.name || source.id || 'unknown',
                sourceFaction: source.faction,
                health: `${this.health - damage}/${this.maxHealth}`
            });
        }
        this.health = Math.max(0, this.health - damage);
        this.updateHealthBar();

        // 显示伤害数值（只在玩家攻击时显示）
        const player = this.game && this.game.getGameObject ? this.game.getGameObject('player') : null;
        const isPlayerAttack = source && (source.faction === 'player' || source === player);
        if (player && player.showDamageNumber && this.mesh && isPlayerAttack) {
            player.showDamageNumber(damage, new THREE.Vector3().copy(this.mesh.position), false);
        }

        // Visual feedback
        this.flashDamage();

        // Spawn hit effect even on non-lethal hits (throttled) with intensity based on remaining HP
        if (player && player.createShipHitEffect && this.mesh) {
            const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            if (now - this.lastHitEffect > this.hitEffectCooldown) {
                const ratio = this.maxHealth ? this.health / this.maxHealth : 0.5;
                const lethal = this.health <= 0;
                const intensity = lethal ? 0.9 : Math.max(0.15, Math.min(0.7, 0.15 + (1 - ratio) * 0.4));
                player.createShipHitEffect(new THREE.Vector3().copy(this.mesh.position), { intensity, lethal });
                if (!lethal && player.createShipFireEffect) {
                    player.createShipFireEffect(new THREE.Vector3().copy(this.mesh.position), intensity * 0.9, { attachTo: this.mesh });
                }
                this.lastHitEffect = now;
            }
        }

        if (this.health <= 0) {
            this.destroy(source || this.lastAttacker);
        }

    }

    // 恢复到满血（固定为5血）
    healToFull() {
        this.health = this.maxHealth;
        this.updateHealthBar();
    }

    flashDamage() {
        if (!this.mesh) return;

        // Use particle effect instead of color flash to avoid material sharing issues
        const player = this.game && this.game.getGameObject ? this.game.getGameObject('player') : null;
        if (player && player.createShipHitEffect) {
            const hitPosition = new THREE.Vector3().copy(this.mesh.position);
            // Small hit effect with low intensity
            player.createShipHitEffect(hitPosition, { intensity: 0.25, lethal: false });
        }
    }

    destroy(killer = null) {
        if (this.shouldRemove) {
            return;
        }

        const resolvedKiller = killer || this.lastAttacker || this.killerRef || null;
        if (resolvedKiller) {
            this.killerRef = resolvedKiller;
            this.killerFaction = resolvedKiller.faction || this.killerFaction || null;
        }

        // Create explosion
        if (this.game && this.game.getGameObject('player') && this.mesh) {
            this.game.getGameObject('player').createExplosion(this.mesh.position);
        }

        // Drop coins when destroyed
        if (this.game && this.game.coinSystem && this.mesh) {
            const dropPosition = this.mesh ? new THREE.Vector3().copy(this.mesh.position) : (this.body ? new THREE.Vector3().copy(this.body.position) : new THREE.Vector3());
            const killerFaction = this.killerFaction || (this.killerRef && this.killerRef.faction) || null;

            if (killerFaction === 'enemy' && this.killerRef && this.killerRef.id) {
                // 敌船击杀敌船：直接获取2倍金币，不掉落
                const earnedGold = this.coinValue * 2;
                this.killerRef.collectedCoins = (this.killerRef.collectedCoins || 0) + earnedGold;

                // 更新排行榜
                if (this.game.rankingSystem && this.killerRef.baseName) {
                    this.game.rankingSystem.saveEnemyGold(this.killerRef.baseName, this.killerRef.collectedCoins);
                }

                console.log(`💰 ${this.killerRef.baseName || 'Enemy'} killed enemy and earned ${earnedGold} coins (2x bonus)`);
            } else {
                // 玩家击杀敌船：正常掉落金币
                const options = { collectorType: 'player' };
                this.game.coinSystem.dropCoins(dropPosition, this.coinValue, options);
            }
        }

        // Mark for removal instead of removing directly
        this.shouldRemove = true;
    }

    handleCollision(event) {
        const otherBody = event.target === this.body ? event.body : event.target;

        // 炮弹伤害由炮弹自身的碰撞回调统一结算，避免重复扣血
        // 这里可以根据需要添加非炮弹的碰撞处理（例如与建筑物等），当前不处理伤害
        if (!otherBody || !otherBody.userData) return;
    }

    // AI Combat System - 攻击锁定的目标
    checkPlayerAndShoot(allowTargetScan = true) {
        if (!this.body || !this.game) return;

        const now = Date.now();

        if (allowTargetScan) {
            if (!this.currentShootingTarget || now - this.targetLockTime > this.targetLockDuration) {
                const nearestTarget = this.findNearestTarget();
                if (nearestTarget) {
                    this.currentShootingTarget = nearestTarget;
                    this.targetLockTime = now;
                    this.targetLockDuration = 3000 + Math.random() * 4000;
                } else {
                    this.currentShootingTarget = null;
                }
            }
        } else if (this.currentShootingTarget && now - this.targetLockTime > this.targetLockDuration) {
            // 轻量模式下到期时清除目标，等待下一次扫描
            this.currentShootingTarget = null;
            this.targetLockTime = 0;
        }

        if (!this.currentShootingTarget) {
            return;
        }

        const target = this.currentShootingTarget.entity;
        if (!target || !target.body || target.health <= 0) {
            this.currentShootingTarget = null;
            this.targetLockTime = 0;
            return;
        }

        this.currentShootingTarget.position.copy(target.body.position);

        const enemyPos = this._scratch.myPos.copy(this.body.position);
        const targetPos = this.currentShootingTarget.position;
        const distanceToTarget = enemyPos.distanceTo(targetPos);

        const isPlayerTarget = this.currentShootingTarget.type === 'player';
        const effectiveRange = isPlayerTarget ? this.shootingRange * 1.05 : this.shootingRange;

        if (distanceToTarget <= effectiveRange && this.lastFireTime <= 0) {
            const fireChance = isPlayerTarget ? 0.35 : 0.25;
            if (Math.random() < fireChance) {
                this.shootAtTarget(targetPos, this.currentShootingTarget.name);
            } else {
                const retryDelay = Math.max(1200, this.cannonCooldown * 1000 * 0.45);
                this.lastFireTime = retryDelay;
            }
        }
    }

    refreshChaseOffset(now) {
        if (!this.chaseOffset || now >= this.nextChaseOffsetTime) {
            this.chaseOffset = this.generateChaseOffset();
            this.nextChaseOffsetTime = now + this.chaseOffsetInterval;
        }
    }

    generateChaseOffset(forceWide = false) {
        const radiusMin = forceWide ? 18 : 10;
        const radiusMax = forceWide ? 26 : 18;
        const radius = radiusMin + Math.random() * (radiusMax - radiusMin);
        const angle = Math.random() * Math.PI * 2;
        return new THREE.Vector3(
            Math.cos(angle) * radius,
            0,
            Math.sin(angle) * radius
        );
    }

    getChasePositionTowards(targetPosition, outVec = this._scratch.aux2) {
        const desired = outVec.copy(targetPosition);
        if (!this.chaseOffset) {
            this.chaseOffset = this.generateChaseOffset();
        }
        desired.add(this.chaseOffset);
        const boundaryLimit = GAME_BOUNDARY_SIZE - 5;
        desired.x = Math.max(-boundaryLimit, Math.min(boundaryLimit, desired.x));
        desired.z = Math.max(-boundaryLimit, Math.min(boundaryLimit, desired.z));
        desired.y = 0.1;
        return desired;
    }

    computeSeparationOffset(myPos, outVec = this._scratch.separation) {
        if (!this.body || !this.game?.enemyManager) {
            return outVec.set(0, 0, 0);
        }

        const separation = outVec.set(0, 0, 0);
        let neighborCount = 0;
        const separationDistance = 8 + this.level * 0.6;
        const separationDistanceSq = separationDistance * separationDistance;
        const otherPos = this._scratch.aux3;
        const pushVector = this._scratch.push;

        this.game.enemyManager.enemies.forEach((enemy) => {
            if (!enemy || enemy.id === this.id || !enemy.body || enemy.health <= 0) return;

            otherPos.copy(enemy.body.position);
            const dx = myPos.x - otherPos.x;
            const dz = myPos.z - otherPos.z;
            const distanceSq = dx * dx + dz * dz;

            if (distanceSq > 0 && distanceSq < separationDistanceSq) {
                const distance = Math.sqrt(distanceSq);
                const pushStrength = (separationDistance - distance) / separationDistance;
                pushVector.set(dx, 0, dz).normalize().multiplyScalar(pushStrength * 6);
                separation.add(pushVector);
                neighborCount++;
            }
        });

        if (neighborCount > 0) {
            separation.divideScalar(neighborCount);
        }

        const maxOffset = 12;
        if (separation.lengthSq() > maxOffset * maxOffset) {
            separation.setLength(maxOffset);
        }

        return separation;
    }

    // 寻找最近的其他敌船（用于AI追击）
    findNearestEnemy(myPos, outPosition) {
        if (!this.body || !this.game || !this.game.enemyManager) return null;

        let nearestDistanceSq = Infinity;

        this.game.enemyManager.enemies.forEach((enemy) => {
            if (enemy.id === this.id || !enemy.body || enemy.health <= 0) return;

            const bodyPos = enemy.body.position;
            const dx = myPos.x - bodyPos.x;
            const dz = myPos.z - bodyPos.z;
            const distanceSq = dx * dx + dz * dz;

            if (distanceSq < nearestDistanceSq) {
                nearestDistanceSq = distanceSq;
                outPosition.set(bodyPos.x, bodyPos.y, bodyPos.z);
            }
        });

        if (nearestDistanceSq === Infinity) {
            return null;
        }

        return Math.sqrt(nearestDistanceSq);
    }

    // 选择攻击目标：引入随机性，避免所有敌船盯着最近单位
    findNearestTarget() {
        if (!this.body || !this.game) return null;

        const scratch = this._scratch;
        const myPos = scratch.myPos.copy(this.body.position);
        const buffer = this._targetBuffer;
        let bestScore = 0;
        let found = false;

        const evaluateTarget = (entity, type, name, bias) => {
            if (!entity || !entity.body || entity.health <= 0) {
                return;
            }

            const candidatePos = scratch.aux.copy(entity.body.position);
            const distance = myPos.distanceTo(candidatePos);
            if (distance > this.shootingRange) {
                return;
            }

            const randomFactor = 0.95 + Math.random() * 0.1;
            const score = (bias * randomFactor) / Math.pow(distance + 1, 0.6);
            if (score > bestScore) {
                bestScore = score;
                buffer.position.copy(candidatePos);
                buffer.name = name;
                buffer.type = type;
                buffer.entity = entity;
                buffer.distance = distance;
                found = true;
            }
        };

        const player = this.game.getGameObject('player');
        evaluateTarget(player, 'player', '玩家', 1.2);

        if (this.game.enemyManager && this.game.enemyManager.enemies) {
            this.game.enemyManager.enemies.forEach((enemy) => {
                if (enemy.id === this.id) return;
                evaluateTarget(enemy, 'enemy', enemy.name, 1);
            });
        }

        return found ? buffer : null;
    }

    shootAtTarget(targetPosition, targetName) {
        if (!this.body || this.lastFireTime > 0) return;

        // Start cooldown
        this.lastFireTime = this.cannonCooldown * 1000; // Convert to milliseconds

        // Calculate shooting position (from enemy ship front)
        const shootPos = new THREE.Vector3();
        this.mesh.getWorldPosition(shootPos);

        // Move firing position forward from ship
        const forward = new THREE.Vector3(0, 0, 1);
        forward.applyQuaternion(this.mesh.quaternion);
        shootPos.add(forward.multiplyScalar(3));

        // Calculate direction to target with accuracy variation
        const direction = new THREE.Vector3().subVectors(targetPosition, shootPos).normalize();

        // Add inaccuracy to make AI more realistic
        const inaccuracy = 1 - this.shootingAccuracy;
        direction.x += (Math.random() - 0.5) * inaccuracy;
        direction.z += (Math.random() - 0.5) * inaccuracy;
        direction.normalize();

        // Create enemy cannonball
        this.createEnemyCannonball(shootPos, direction);

    }

    createEnemyCannonball(position, direction) {
        // Create enemy cannonball (similar to player but with different properties)
        const cannonballGeometry = new THREE.SphereGeometry(0.6, 12, 12);
        const cannonballMaterial = new THREE.MeshLambertMaterial({
            color: 0x8B0000 // Dark red for enemy cannonballs
        });

        const mesh = new THREE.Mesh(cannonballGeometry, cannonballMaterial);
        mesh.position.copy(position);
        mesh.castShadow = true;

        // Create physics body
        const body = this.game.physicsSystem.createProjectileBody(position, 0.6);

        // Set collision properties
        body.collisionFilterMask = 1 | 2 | 8; // Collide with boundaries, ships, buildings

        // Set initial velocity
        const velocity = direction.clone().multiplyScalar(24 + this.level * 1.5);
        body.velocity.set(velocity.x, velocity.y, velocity.z);

        // Mark as enemy projectile
        body.userData = {
            type: 'cannonball',
            shooter: this.body, // Reference to enemy ship that shot it
            shooterMesh: this.mesh,
            isEnemyProjectile: true,
            damage: this.attackPower
        };

        console.log(`Enemy ${this.name} (L${this.level}) fired cannonball with damage: ${this.attackPower}`); // 调试信息

        // Add collision event listener
        body.addEventListener('collide', (event) => {
            this.handleCannonballCollision(event, mesh, body);
        });

        const boundaryLimit = GAME_BOUNDARY_SIZE;

        // Create cannonball object
        const cannonballObject = {
            mesh: mesh,
            body: body,
            lifeTime: 0,
            maxLifeTime: 8, // 8 seconds life

            update: (deltaTime) => {
                // Sync mesh with physics body
                if (mesh && body) {
                    this.game.physicsSystem.constructor.syncMeshWithBody(mesh, body);
                }

                // Update lifetime
                cannonballObject.lifeTime += deltaTime;

                // Check for removal conditions
                if (cannonballObject.lifeTime > cannonballObject.maxLifeTime ||
                    Math.abs(body.position.x) > boundaryLimit ||
                    Math.abs(body.position.z) > boundaryLimit) {

                    // Create small explosion
                    if (this.game.getGameObject('player')) {
                        this.game.getGameObject('player').createExplosion(new THREE.Vector3().copy(body.position));
                    }

                    cannonballObject.shouldRemove = true;
                }
            },

            dispose: () => {
                if (mesh) {
                    mesh.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) {
                            if (Array.isArray(child.material)) {
                                child.material.forEach(mat => mat.dispose());
                            } else {
                                child.material.dispose();
                            }
                        }
                    });
                }
            }
        };

        // Add to game objects
        const cannonballId = `enemy_cannonball_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.game.addGameObject(cannonballId, cannonballObject);

        return cannonballObject;
    }

    handleCannonballCollision(event, mesh, body) {
        const otherBody = event.target === body ? event.body : event.target;

        // Don't collide with shooter
        if (body.userData && body.userData.shooter === otherBody) {
            return;
        }

        // Create explosion effect
        const playerEntity = this.game.getGameObject('player');
        if (playerEntity) {
            playerEntity.createExplosion(new THREE.Vector3().copy(body.position));
        }

        // Check what was hit
        const collisionGroup = otherBody.collisionFilterGroup;

        if (collisionGroup === 2) {
            // Hit a ship (player or enemy)
            if (otherBody && otherBody.userData && otherBody.userData.entity) {
                const target = otherBody.userData.entity;
                const damage = body.userData && body.userData.damage ? body.userData.damage : 1;

                // Apply damage to target
                if (target.takeDamage) {
                    target.takeDamage(damage, this);
                    console.log(`Enemy ${this.name} cannonball hit ${target.name || 'Player'} with ${damage} damage`);

                    // Play hit sound effect only when hitting player
                    if (this.game && this.game.audioManager && target.faction === 'player') {
                        this.game.audioManager.playCannonHit();
                    }
                }

                // Create ship hit effect with intensity based on target remaining HP
                if (playerEntity) {
                    const remaining = Math.max(0, target.health - damage);
                    const ratio = target.maxHealth ? remaining / target.maxHealth : 0.5;
                    const lethal = remaining <= 0;
                    const intensity = lethal ? 0.85 : Math.max(0.15, Math.min(0.6, 0.15 + (1 - ratio) * 0.4));
                    playerEntity.createShipHitEffect(new THREE.Vector3().copy(body.position), { intensity });
                }
            }
        } else if (collisionGroup === 8) {
            // Create building hit effect
            if (this.game.getGameObject('player')) {
                this.game.getGameObject('player').createBuildingHitEffect(new THREE.Vector3().copy(body.position));
            }
        }

        // Mark cannonball for removal
        this.game.gameObjects.forEach((gameObject, id) => {
            if (gameObject.body === body) {
                gameObject.shouldRemove = true;
            }
        });
    }

    // Health bar creation and management
    // 3D UI方法已被移除，现在使用屏幕空间UI管理器

    updateHealthBar() {
        // 血条现在由屏幕空间UI管理器处理
        // 只需要在updateScreenSpaceUI中更新即可
    }

    // 所有3D UI创建方法已被移除，现在使用EnemyUIManager处理屏幕空间UI

    updateEscorts(deltaTime) {
        // 清理已销毁的副船
        const escortsToRemove = [];
        this.escorts.forEach((escort, index) => {
            if (!escort || escort.shouldRemove || escort.health <= 0) {
                escortsToRemove.push(index);
                if (escort && escort.id) {
                    this.game.removeGameObject(escort.id);
                }
                if (escort && escort.dispose) {
                    escort.dispose();
                }
            }
        });

        // 从后往前删除，避免索引问题
        for (let i = escortsToRemove.length - 1; i >= 0; i--) {
            this.escorts.splice(escortsToRemove[i], 1);
        }

        // 更新所有副船
        this.escorts.forEach(escort => {
            if (escort && escort.update) {
                escort.update(deltaTime);
            }
        });
    }

    // 给敌船添加副船（由EnemyManager调用）
    async addEscort() {
        // 增强检查：确保敌船自己的物理体完全准备好
        if (this.escorts.length >= this.maxEscorts || !this.body || !this.body.position || !this.body.quaternion) {
            console.warn(`敌船 ${this.name} 无法添加副船：物理体未准备好或已达上限`);
            return null;
        }

        const { EscortBoat } = await import('./EscortBoat.js');

        const startPos = new THREE.Vector3().copy(this.body.position);
        startPos.y = 0.1;

        const escort = new EscortBoat({
            game: this.game,
            manager: this, // 使用敌船作为manager
            attackPower: this.escortAttackPower,
            formationIndex: this.escorts.length,
            ownerShip: this, // 标记所属敌船
            isEnemyEscort: true // 标记为敌船副船
        });

        await escort.init(startPos);

        // 修改副船的faction
        escort.faction = 'enemy';
        if (escort.body && escort.body.userData) {
            escort.body.userData.type = 'enemy';
            escort.body.userData.entity = escort;
        }
        if (escort.mesh && escort.mesh.userData) {
            escort.mesh.userData.type = 'enemy';
            escort.mesh.userData.entity = escort;
        }

        const escortId = `enemy_escort_${this.id}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        escort.id = escortId;
        this.game.addGameObject(escortId, escort);
        this.escorts.push(escort);

        console.log(`敌船 ${this.name} 添加了副船`);
        return escort;
    }

    // 提供formation offset（副船跟随逻辑）
    getFormationOffset(index) {
        const offsets = [
            new THREE.Vector2(0, -4.5),
            new THREE.Vector2(-1.5, -5.5),
            new THREE.Vector2(1.5, -5.5),
            new THREE.Vector2(-2.5, -7.5),
            new THREE.Vector2(2.5, -7.5)
        ];
        if (index >= 0 && index < offsets.length) {
            return offsets[index];
        }
        return new THREE.Vector2(0, -4.5 - index * 1.25);
    }

    // 副船被摧毁时调用
    handleFollowerDestroyed(escort) {
        const index = this.escorts.indexOf(escort);
        if (index !== -1) {
            this.escorts.splice(index, 1);
        }
    }

    dispose() {
        if (this.mesh) {
            this.mesh.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => material.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
        }

        // 清理副船
        const escortsToRemove = [...this.escorts]; // 复制数组避免在遍历时修改
        escortsToRemove.forEach(escort => {
            if (escort) {
                escort.shouldRemove = true; // 标记为应该移除
                if (escort.id) {
                    this.game.removeGameObject(escort.id);
                }
                if (escort.dispose) {
                    escort.dispose();
                }
            }
        });
        this.escorts = [];

        // 清理屏幕空间UI
        if (this.game.enemyUIManager) {
            this.game.enemyUIManager.removeEnemyUI(this.id);
        }

        // 清理旧的3D血条（如果存在）
        if (this.healthBarContainer && this.game && this.game.sceneManager) {
            this.game.sceneManager.remove(this.healthBarContainer);
            this.healthBarContainer = null;
        }

        // 旧的精灵清理代码已移除，现在由EnemyUIManager处理

        if (this.body && this.game && this.game.physicsSystem) {
            this.game.physicsSystem.removeBody(this.body);
        }
    }
}
